import { useId, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

import type { MapLineData, MapSchematicGeometry, MapSchematicPoint } from "./api";
import { displayLocale } from "./localization";
import { armDirection, clampSchematicPoint, roadArmForLine, schematicGate, schematicLineAnchors, schematicPathData, schematicPathKey } from "./schematicGeometry";

export type SchematicFlow = {
  entryLineID: string;
  exitLineID: string;
  total: number;
};

type Props = {
  geometry: MapSchematicGeometry;
  lines: MapLineData[];
  flows?: SchematicFlow[];
  selectedPathKey?: string;
  editable?: boolean;
  addingWaypoint?: boolean;
  selectedWaypoint?: number | null;
  onSelectPath?: (key: string) => void;
  onAddWaypoint?: (point: MapSchematicPoint) => void;
  onMoveWaypoint?: (index: number, point: MapSchematicPoint) => void;
  onSelectWaypoint?: (index: number) => void;
};

function pointsAttribute(points: MapSchematicPoint[]) {
  return points.map((point) => point.join(",")).join(" ");
}

function schematicRoadWidth(widthMetres: number) {
  return Math.min(124, Math.max(46, widthMetres * 10));
}

export function SchematicIntersection({ geometry, lines, flows = [], selectedPathKey = "", editable = false, addingWaypoint = false, selectedWaypoint = null, onSelectPath, onAddWaypoint, onMoveWaypoint, onSelectWaypoint }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [draggedWaypoint, setDraggedWaypoint] = useState<number | null>(null);
  const schematicID = useId().replace(/:/g, "");
  const markerID = `schematic-arrow-${schematicID}`;
  const roadEdgeMaskID = `schematic-road-edge-${schematicID}`;
  const roadSurfaceMaskID = `schematic-road-surface-${schematicID}`;
  const roadCenterMaskID = `schematic-road-center-${schematicID}`;
  const lineNames = new Map(lines.map((line) => [line.id, line.name]));
  const lineAnchors = editable ? schematicLineAnchors(geometry, lines) : undefined;
  const gateAnchors = lineAnchors
    ? Array.from(lineAnchors.entries()).map(([lineID, arm]) => ({ lineID, arm, demo: !roadArmForLine(geometry, lineID) }))
    : geometry.arms.filter((arm) => arm.line_id).map((arm) => ({ lineID: arm.line_id!, arm, demo: false }));
  const flowValues = new Map(flows.map((flow) => [schematicPathKey(flow.entryLineID, flow.exitLineID), flow.total]));
  const maximumFlow = Math.max(1, ...flows.map((flow) => flow.total));
  const junctionMarkingRadius = Math.min(112, Math.max(58, ...geometry.arms.map((arm) => schematicRoadWidth(arm.width_m) * .9)));
  const visiblePaths = geometry.paths.filter((path) => {
    if (path.mode === "disabled") return false;
    const key = schematicPathKey(path.entry_line_id, path.exit_line_id);
    if (!editable) return (flowValues.get(key) ?? 0) > 0;
    const selected = geometry.paths.find((candidate) => schematicPathKey(candidate.entry_line_id, candidate.exit_line_id) === selectedPathKey);
    return key === selectedPathKey || path.entry_line_id === selected?.entry_line_id;
  });
  const selectedPath = geometry.paths.find((path) => schematicPathKey(path.entry_line_id, path.exit_line_id) === selectedPathKey);

  function eventPoint(event: Pick<ReactPointerEvent<SVGSVGElement>, "clientX" | "clientY">): MapSchematicPoint | null {
    const svg = svgRef.current;
    const matrix = svg?.getScreenCTM();
    if (!svg || !matrix) return null;
    const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix.inverse());
    return clampSchematicPoint([point.x, point.y]);
  }

  function moveWaypoint(event: ReactPointerEvent<SVGSVGElement>) {
    if (draggedWaypoint === null || !onMoveWaypoint) return;
    const point = eventPoint(event);
    if (point) onMoveWaypoint(draggedWaypoint, point);
  }

  function stopDragging(event: ReactPointerEvent<SVGSVGElement>) {
    if (draggedWaypoint !== null) event.currentTarget.releasePointerCapture(event.pointerId);
    setDraggedWaypoint(null);
  }

  return <div className={`schematic-intersection${editable ? " editable" : ""}`}>
    <svg ref={svgRef} viewBox="0 0 1000 1000" role="img" aria-label="Schematische Kreuzungsansicht" onPointerMove={moveWaypoint} onPointerUp={stopDragging} onPointerCancel={stopDragging} onClick={(event) => {
      if (!addingWaypoint || !onAddWaypoint) return;
      const point = eventPoint(event);
      if (point) onAddWaypoint(point);
    }}>
      <defs>
        <marker id={markerID} className="schematic-arrowhead" viewBox="0 0 30 26" markerWidth="30" markerHeight="26" refX="28" refY="13" orient="auto" markerUnits="userSpaceOnUse"><path d="M 0 0 L 30 13 L 0 26 z" /></marker>
        <mask id={roadEdgeMaskID} className="schematic-road-union-mask" maskUnits="userSpaceOnUse" x="0" y="0" width="1000" height="1000">
          <rect x="0" y="0" width="1000" height="1000" fill="black" />
          {geometry.arms.map((arm) => <polyline key={arm.id} points={pointsAttribute(arm.centerline)} fill="none" stroke="white" strokeWidth={schematicRoadWidth(arm.width_m) + 5} strokeLinecap="round" strokeLinejoin="round" />)}
        </mask>
        <mask id={roadSurfaceMaskID} className="schematic-road-union-mask" maskUnits="userSpaceOnUse" x="0" y="0" width="1000" height="1000">
          <rect x="0" y="0" width="1000" height="1000" fill="black" />
          {geometry.arms.map((arm) => <polyline key={arm.id} points={pointsAttribute(arm.centerline)} fill="none" stroke="white" strokeWidth={schematicRoadWidth(arm.width_m)} strokeLinecap="round" strokeLinejoin="round" />)}
        </mask>
        <mask id={roadCenterMaskID} className="schematic-road-center-mask" maskUnits="userSpaceOnUse" x="0" y="0" width="1000" height="1000">
          <rect x="0" y="0" width="1000" height="1000" fill="white" />
          <circle cx="500" cy="500" r={junctionMarkingRadius} fill="black" />
        </mask>
      </defs>
      <rect className="schematic-background" x="0" y="0" width="1000" height="1000" />
      <g className="schematic-road-network">
        <rect className="schematic-road-network-edge" x="0" y="0" width="1000" height="1000" mask={`url(#${roadEdgeMaskID})`} />
        <rect className="schematic-road-network-surface" x="0" y="0" width="1000" height="1000" mask={`url(#${roadSurfaceMaskID})`} />
      </g>
      <g className="schematic-roads">{geometry.arms.map((arm) => {
        const direction = armDirection(arm);
        const labelPoint = arm.centerline.at(-1) ?? [500, 80];
        const labelX = labelPoint[0] - direction.x * 34;
        const labelY = labelPoint[1] - direction.y * 34;
        return <g key={arm.id} className="schematic-road-arm">
          <polyline className="schematic-road-center" points={pointsAttribute(arm.centerline)} mask={`url(#${roadCenterMaskID})`} />
          <text x={labelX} y={labelY} textAnchor="middle"><tspan>{arm.name}</tspan><tspan x={labelX} dy="18">{arm.width_m.toLocaleString(displayLocale, { maximumFractionDigits: 1 })} m · {arm.width_source === "mapped" ? "kartiert" : arm.width_source === "manual" ? "manuell" : "abgeleitet"}</tspan></text>
        </g>;
      })}</g>
      <g className="schematic-gates">{gateAnchors.map(({ lineID, arm, demo }) => {
        const gate = schematicGate(arm);
        return <g key={lineID} className={demo ? "demo" : undefined}><line x1={gate[0][0]} y1={gate[0][1]} x2={gate[1][0]} y2={gate[1][1]} /><text x={(gate[0][0] + gate[1][0]) / 2} y={(gate[0][1] + gate[1][1]) / 2 - 10} textAnchor="middle">{lineNames.get(lineID) ?? lineID}{demo ? " · Demo" : ""}</text></g>;
      })}</g>
      <g className="schematic-paths">{visiblePaths.map((path) => {
        const key = schematicPathKey(path.entry_line_id, path.exit_line_id);
        const selected = key === selectedPathKey;
        const total = flowValues.get(key) ?? 0;
        const label = path.entry_line_id === path.exit_line_id ? `${lineNames.get(path.entry_line_id) ?? path.entry_line_id} · U-Turn` : `${lineNames.get(path.entry_line_id) ?? path.entry_line_id} nach ${lineNames.get(path.exit_line_id) ?? path.exit_line_id}`;
        return <path key={key} d={schematicPathData(geometry, path, lineAnchors)} className={`schematic-flow-path${selected ? " selected" : ""}${path.entry_line_id === path.exit_line_id ? " uturn" : ""}`} style={{ strokeWidth: editable ? selected ? 8 : 4 : 4 + total / maximumFlow * 15 }} markerEnd={`url(#${markerID})`} aria-label={`${label}${editable ? "" : `: ${total}`}`} tabIndex={editable ? 0 : undefined} onClick={(event) => {
          if (addingWaypoint) return;
          event.stopPropagation();
          onSelectPath?.(key);
        }} />;
      })}</g>
      {editable && selectedPath?.mode === "manual" && <g className="schematic-waypoints">{selectedPath.waypoints.map((point, index) => <g key={index} transform={`translate(${point[0]} ${point[1]})`} className={selectedWaypoint === index ? "selected" : ""} onPointerDown={(event) => {
        event.preventDefault(); event.stopPropagation(); event.currentTarget.ownerSVGElement?.setPointerCapture(event.pointerId); setDraggedWaypoint(index); onSelectWaypoint?.(index);
      }} onClick={(event) => { event.preventDefault(); event.stopPropagation(); onSelectWaypoint?.(index); }}><circle r="15" /><text y="4" textAnchor="middle">{index + 1}</text></g>)}</g>}
      {addingWaypoint && <text className="schematic-add-hint" x="500" y="950" textAnchor="middle">In die Skizze klicken, um einen Kontrollpunkt hinzuzufügen</text>}
    </svg>
    <a className="schematic-attribution" href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">{geometry.source.copyright}</a>
  </div>;
}
